import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { PipelineMasterSteps } from 'global-stores';
import { useOnboardingHook } from 'views/onboarding-flow';
import { ActionList } from 'views/complex-onboarding-flow/constant';
import { ComplexSettingFormState } from 'views/pipeline';
import { CustomHandle } from '../custom-handle';
import { ICustomNode } from '../custom-node';
import { RenderKybBlock } from './render-kyb-block';
import './kyb-action.scss';
import { TileTotal } from '../tile-total';

export const KYBWrapper = ({ children }: { children: ReactNode }) => {
	return <div className="kyb-action">{children}</div>;
};
const StepIds = ['Verify information about a business entity'];

export const KybAction: FC<ICustomNode> = ({ id: nodeId, data }) => {
	const pipelinesSteps = useRecoilValue(PipelineMasterSteps);
	const [complexSettingForm, setComplexSettingForm] = useRecoilState(
		ComplexSettingFormState
	);

	const actionId = useMemo(() => data.label, [data.label]);

	const actions = useMemo(
		() =>
			pipelinesSteps.find(
				pipelineStep => pipelineStep.key === (ActionList as any)[actionId]
			)?.actions[0],
		[actionId, pipelinesSteps]
	);

	// Arun kumar : showing price for kyb node as per the nodeID 
	const totalPrice = useMemo(() => {
		let price = 0;
		for (const item of actions?.metadata ?? []) {
			if (
				nodeId in complexSettingForm &&
				complexSettingForm[nodeId][item.key] === true
			) {
				price += item.price ?? 0;
			}
		}
		return price;
	}, [actions?.metadata, complexSettingForm, nodeId]);

	// __simplici_kyb_tile_checkboxes__: the optional checks, offered as rows on the
	// tile. Read from the catalog, so labels and prices are the ones
	// /pipeline-init already resolved against this business's own rates -- and a
	// check withdrawn from the catalog leaves the tile with no edit here.
	const { isPricingHide } = useOnboardingHook();
	const kybOptions = useMemo(() => {
		const OPTION_KEYS = [
			'representative_kyc_aml_verification',
			'business_sanctions_ofac',
			// Per-person PEPs & sanctions on the roster the registry returned.
			// Default OFF, and deliberately absent from the force-tick list below:
			// it bills per member, so it is only ever bought on purpose.
			'members_executives_pep_sanctions',
		];
		return ((actions?.metadata ?? []) as any[])
			.filter(item => OPTION_KEYS.includes(item?.key))
			.map(item => ({
				key: item.key as string,
				label: item.label as string,
				price: Number(item.price ?? 0),
			}));
	}, [actions?.metadata]);

	// The base row names ONE check, so it shows that check's price -- not the tile
	// total, which would double-report it once the optional rows can be switched
	// on. Falls back to the action price when the catalog prices no metadata.
	const basePrice = useMemo(() => {
		const enhanced = ((actions?.metadata ?? []) as any[]).find(
			item => item?.key === 'kyb_enhanced'
		);
		return Number(enhanced?.price ?? (actions as any)?.price ?? 0);
	}, [actions]);

	const toggleOption = useCallback(
		(key: string) => {
			setComplexSettingForm((prev: any) => {
				const next = structuredClone(prev);
				if (!next[nodeId]) next[nodeId] = {};
				next[nodeId][key] = next[nodeId][key] !== true;
				return next;
			});
		},
		[nodeId, setComplexSettingForm]
	);

	const renderActionLayout = useMemo(
		() =>
			StepIds.map((el: string) => (
				<RenderKybBlock
					stepId="kyb"
					label={actions?.label}
					key={el}
					price={basePrice}
					nodeId={nodeId}
				/>
			)),
		[actions?.label, basePrice, nodeId]
	);

	//Shahbaaz Set Kyb on complex config setting form for showing price breakdown
	useEffect(() => {
		// Arun Kumar : Action added in state for KYB in ComplexSettingFormState
		const kybActions = pipelinesSteps?.filter(item => {
			if (item.actions) {
				return item.actions.some(
					(action: { key: string }) => action.key === data?.label
				);
			}
			return [];
		});
		const metadatas = kybActions[0]?.actions[0]?.metadata || [];
		let expectedResult: any = {};

		metadatas.forEach(item => {
			if (item.key && item.default) {
				expectedResult[item.key] = item.default === 'true';
			}
		});
		// __simplici_kyb_tile_checkboxes__: the two optional checks start TICKED.
		// This OVERRIDES the catalog, which ships them default=false -- so a
		// null-guard would never fire, the loop above having already written `false`.
		//
		// Placed BEFORE the saved-config branch on purpose: a node that already has
		// settings keeps them (that branch replaces expectedResult wholesale), so an
		// explicit untick survives and is not silently switched back on.
		//
		// NOTE this bills: a new KYB step now quotes $6.45 rather than $5.00 unless
		// the extra checks are unticked.
		['representative_kyc_aml_verification', 'business_sanctions_ofac'].forEach(
			(k: string) => {
				if (k in expectedResult) expectedResult[k] = true;
			}
		);
		// Arun kumar : Showing saved config to config modal
		if(complexSettingForm?.[nodeId]){
			expectedResult = complexSettingForm?.[nodeId]
		}
		setComplexSettingForm((prev: any) => {
			const newObj = structuredClone(prev);
			if (!newObj[nodeId]) {
				newObj[nodeId] = {};
			}
			Object.assign(newObj[nodeId], {
				Kyb: 'Kyb',
				...expectedResult,
			});
			// KYB provider selection: keep a previously-saved object; else seed "Business default".
			// (The metadata loop above coerces defaults to booleans, so set the provider explicitly.)
			const savedKybProvider = newObj[nodeId].kybProvider;
			if (!savedKybProvider || typeof savedKybProvider !== 'object') {
				newObj[nodeId].kybProvider = {
					label: 'Business default',
					value: 'businessDefault',
				};
			}

			return newObj;
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.label, nodeId, pipelinesSteps, setComplexSettingForm]);

	return (
		<KYBWrapper>
			{renderActionLayout}
			{/* __simplici_kyb_tile_checkboxes__ */}
			{!!kybOptions.length && (
				<div className="kyb-action__options">
					{kybOptions.map(option => (
						/* `nodrag` + stopPropagation: this tile is a react-flow node, and
						   without them clicking the row starts a node drag instead. */
						<label
							className="kyb-action__option nodrag"
							key={option.key}
							onClick={e => e.stopPropagation()}
						>
							<input
								type="checkbox"
								className="nodrag"
								checked={complexSettingForm?.[nodeId]?.[option.key] === true}
								onChange={() => toggleOption(option.key)}
							/>
							<span className="kyb-action__option-label">
								{option.label}
							</span>
							{isPricingHide && !!option.price && (
								<span className="kyb-action__option-price">
									${option.price.toFixed(2)}
									<span className="kyb-action__option-unit">
										/Verification
									</span>
								</span>
							)}
						</label>
					))}
				</div>
			)}
			<TileTotal amount={totalPrice} nodeId={nodeId} />
			<CustomHandle value={nodeId} />
		</KYBWrapper>
	);
};
